import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();
    this.slMinusController?.abort();
    this.slMinusController = new AbortController();
    this.addEventListener('click', event => {
      const minus = event.target instanceof Element ? event.target.closest('button[name="minus"]') : null;
      if (!minus || !this.closest('[data-drawer]')) return;
      const input = this.querySelector('input[data-cart-line]');
      const remove = this.closest('tr[data-key]')?.querySelector('.cart-items__remove:not([hidden])');
      if (!input || Number(input.value) !== 1 || !remove) return;
      const cartItems = this.closest('cart-items-component');
      const line = Number(input.dataset.cartLine);
      if (line < 1 || typeof cartItems?.onLineItemRemove !== 'function') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      cartItems.onLineItemRemove(line);
    }, { capture: true, signal: this.slMinusController.signal });
  }
  disconnectedCallback() {
    this.slMinusController?.abort();
    super.disconnectedCallback();
  }

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  decreaseQuantity(event) {
    const { value } = this.getCurrentValues();
    const removeButton = this.closest('[data-drawer]') ? this.closest('.cart-items__table-row')?.querySelector('.cart-items__remove:not([hidden])') : null;
    if (value === 1 && removeButton) {
      event.preventDefault();
      event.stopPropagation();
      removeButton.click();
      return;
    }
    super.decreaseQuantity(event);
  }

  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    const removeButton = this.closest('[data-drawer]') ? this.closest('.cart-items__table-row')?.querySelector('.cart-items__remove:not([hidden])') : null;
    minusButton.disabled = value <= min && !(value === 1 && removeButton);
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
